import { projectService, type Project } from '../services/projectService';
import type { EntitySCV2Config } from './entitySCV2Config';

// Field permissions for Project entity
const PROJECT_FIELDS_PERMISSIONS = {
  name: 'none' as const,
  description: 'editable' as const,
  project_features: 'editable' as const,
  head_of_project: 'none' as const,
  project_engineers: 'none' as const,
  kb_id: 'none' as const,
  kb_ids: 'none' as const,
  created_date: 'none' as const,
  last_updated: 'none' as const,
  id: 'none' as const,
  sub_projects: 'none' as const,
  client_id: 'none' as const,
  voice_sessions: 'none' as const,
};

// Field labels and descriptions
const PROJECT_FIELD_LABELS = {
  name: {
    label: 'Project Name',
    description: 'The official name of the project',
  },
  description: {
    label: 'Description',
    labelKey: 'scv2.fields.description',
    description: 'A concise summary of the project (max 1000 chars)',
  },
  project_features: {
    label: 'Product Features',
    labelKey: 'scv2.fields.project_features',
    description: 'Key technical features of the product (max 5000 chars)',
  },
  head_of_project: {
    label: 'Head of Project',
    description: 'The project lead',
  },
  created_date: {
    label: 'Created Date',
    description: 'When the project was created',
  },
};

// Format date helper
const formatDate = (dateString: string, language: string): string => {
  return new Date(dateString).toLocaleDateString(
    language === 'fr' ? 'fr-FR' : 'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );
};

// Project SCV2 Configuration
export const projectSCV2Config: EntitySCV2Config<Project> = {
  type: 'project',
  displayName: 'Projects',

  fieldsPermissions: PROJECT_FIELDS_PERMISSIONS,
  fieldLabels: PROJECT_FIELD_LABELS,

  service: {
    get: (id: string) => projectService.getProject(id),
    update: (id: string, data: Partial<Project>) => projectService.updateProject(id, data),
    delete: (id: string) => projectService.deleteProject(id),
  },

  buildBreadcrumbs: async (project: Project) => {
    return [
      { path: `/scv2/project/${project.id}`, label: `Project: ${project.name}` },
    ];
  },

  buildActionButtons: (_project, handlers, permissions, t) => {
    return (
      <>
        <button
          className="action-btn btn-view-kbs"
          onClick={handlers.onViewKBs}
        >
          {t('projectDetail.viewKBs', 'Voir BCs')}
        </button>
        <button
          className="action-btn btn-view-subprojects"
          onClick={handlers.onViewSubProjects}
        >
          {t('projectDetail.viewSubProjects', 'Voir Sous-projets')}
        </button>
        {permissions.canCreate && (
          <button
            className="action-btn btn-create"
            onClick={handlers.onCreate}
          >
            {t('projectDetail.createSubProject', 'Créer un sous-projet')}
          </button>
        )}
        {permissions.canDelete && (
          <button
            className="action-btn btn-delete"
            onClick={handlers.onDelete}
          >
            {t('projectDetail.deleteProject', 'Supprimer le Projet')}
          </button>
        )}
      </>
    );
  },

  getDetailPath: (project: Project) => `/project/${project.id}`,
  
  getParentPath: () => '/projects',

  formatDate,

  getKbIds: (project: Project) => project.kb_ids,

  getEntityName: (project: Project) => project.name,
};
